import { ListVirtualized } from "@data-display/list/ListVirtualized";
import { Box, Checkbox, Stack } from "@mui/material";
import React, { useMemo } from "react";
import { selectableItemContent } from "./SelectableRow";
import { useListVirtualizedSelect } from "./logics";
import type { IListVirtualizedSelectContext, IListVirtualizedSelectProps } from "./types";

export const ListVirtualizedSelect = <T,>({
  data,
  headerContent,
  itemContent,
  emptyContent,
  loading,
  getId,
  resetSelectedItemsEvent,
  selectedItems: selectedItemsProps,
  onSelectedItemsChange,
  ListHeaderStack,
  ListHeaderCheckbox,
  ListHeaderBox,
  ListItemStack,
  ListItemCheckbox,
  ListItemBox,
  virtuosoRef,
  ...props
}: IListVirtualizedSelectProps<T>) => {
  const { selectedItems, handleSelectAll, handleSelect } = useListVirtualizedSelect(
    data,
    getId,
    resetSelectedItemsEvent,
    selectedItemsProps,
    onSelectedItemsChange
  );

  const context = useMemo(
    () =>
      React.createContext<IListVirtualizedSelectContext<T>>({
        selectedItems,
        handleSelect,
        itemContent,
        getId,
        ListHeaderStack,
        ListHeaderCheckbox,
        ListHeaderBox,
        ListItemStack,
        ListItemCheckbox,
        ListItemBox,
      }),
    [
      selectedItems,
      handleSelect,
      itemContent,
      getId,
      ListItemStack,
      ListItemCheckbox,
      ListItemBox,
      ListHeaderStack,
      ListHeaderCheckbox,
      ListHeaderBox,
    ]
  );

  if (emptyContent && !loading && data.length === 0) {
    return emptyContent();
  } else {
    return (
      <>
        <Stack
          id="list-virtualized-header"
          direction="row"
          spacing={2}
          {...ListHeaderStack}
          sx={[
            {
              alignItems: "center",
            },
            ...(Array.isArray(ListHeaderStack?.sx) ? ListHeaderStack.sx : [ListHeaderStack?.sx]),
          ]}
        >
          <Checkbox
            checked={selectedItems.length === data.length && data.length !== 0}
            indeterminate={selectedItems.length !== 0 && selectedItems.length !== data.length}
            onChange={handleSelectAll}
            {...ListHeaderCheckbox}
          />
          <Box
            {...ListHeaderBox}
            sx={[
              {
                flex: "auto",
              },
              ...(Array.isArray(ListHeaderBox?.sx) ? ListHeaderBox.sx : [ListHeaderBox?.sx]),
            ]}
          >
            {headerContent(selectedItems)}
          </Box>
        </Stack>
        <ListVirtualized
          {...props}
          data={data}
          itemContent={selectableItemContent}
          context={context}
          ref={virtuosoRef}
        />
      </>
    );
  }
};
